// src/padplanner/mapView.js
//
// What a PadPlanner map is drawn on: Imagery (the basemap and the AI PRIME
// capture), Basemap (the basemap alone) or Paper (neither: a plain sheet
// from tokens.css with the authoring grid, paperGrid.js). Paper's features
// may be drawn as outlines only, for a drawing look.
//
// One choice per user for every map that offers it (the plan page in each
// state, and the editor; not the dashboard or the plans list), kept in
// localStorage under the user's id. Storage can be missing or refuse (a
// private window, a quota, a policy): then the choice lasts as long as the
// page, and nothing throws.
//
// Paper has a tone, Dark or Light (paperTheme.js), kept with the rest of the
// choice. Dark is the default and what anything unreadable falls back to, so
// a viewer who never switches sees the Paper they always saw.
import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "../stores/useAuthStore";
import { LINE_COLORS } from "./lineVocabulary";
import { PAPER_TONES, DEFAULT_PAPER_TONE, paperTheme } from "./paperTheme";

export const MAP_VIEW_MODES = Object.freeze(["imagery", "basemap", "paper"]);
export const MAP_VIEW_LABELS = Object.freeze({ imagery: "Imagery", basemap: "Basemap", paper: "Paper" });
export const DEFAULT_MAP_VIEW = Object.freeze({ mode: "imagery", outlines: false, tone: DEFAULT_PAPER_TONE });
export const storageKey = (userId) => `aiprime.padplanner.mapView.v1:${userId ?? "anonymous"}`;

export function readMapView(userId) {
  try {
    const v = JSON.parse(window.localStorage.getItem(storageKey(userId)) ?? "null");
    return {
      mode: MAP_VIEW_MODES.includes(v?.mode) ? v.mode : DEFAULT_MAP_VIEW.mode,
      outlines: typeof v?.outlines === "boolean" ? v.outlines : DEFAULT_MAP_VIEW.outlines,
      tone: PAPER_TONES.includes(v?.tone) ? v.tone : DEFAULT_MAP_VIEW.tone,
    };
  } catch {
    return { ...DEFAULT_MAP_VIEW };
  }
}

export function writeMapView(userId, view) {
  try { window.localStorage.setItem(storageKey(userId), JSON.stringify({ mode: view.mode, outlines: view.outlines, tone: view.tone })); } catch { /* the page keeps it */ }
}

/** {mode, outlines, tone, setMode, setOutlines, setTone}, the signed-in user's. */
export function useMapView() {
  const userId = useAuthStore((s) => s.profile?.id ?? null);
  const [view, setView] = useState(() => readMapView(userId));
  // A different user on the same page (a session switch): theirs.
  useEffect(() => { setView(readMapView(userId)); }, [userId]);
  const update = useCallback((patch) => setView((v) => { const n = { ...v, ...patch }; writeMapView(userId, n); return n; }), [userId]);
  const setMode = useCallback((mode) => { if (MAP_VIEW_MODES.includes(mode)) update({ mode }); }, [update]);
  const setOutlines = useCallback((outlines) => update({ outlines: Boolean(outlines) }), [update]);
  const setTone = useCallback((tone) => { if (PAPER_TONES.includes(tone)) update({ tone }); }, [update]);
  return { ...view, setMode, setOutlines, setTone };
}

/**
 * The FootprintEditor's drawing style for a view (FootprintEditor.setDrawingStyle),
 * and the ChecksLayers' (it reads redzone, witness, deferred). Off Paper, the
 * map's own colors; on Paper, the tone's (paperTheme.js).
 */
export function drawingStyleFor(view) {
  if (view?.mode !== "paper") return { lineColors: LINE_COLORS };
  const t = paperTheme(view.tone);
  // paper: symbols gate on Paper's scale, about 40 ft/in (FootprintEditor symbolMinZoom).
  return {
    ink: t.ink, outlines: Boolean(view.outlines), lineColors: t.lineColors, paper: true,
    select: t.select, zone: t.zone, lineDefault: t.lineDefault,
    redzone: t.redzone, witness: t.witness, deferred: t.deferred,
  };
}

/** Keep an editor's drawing style, and its layout's checks', on the view's. */
export function useEditorDrawingStyle(editor, view, checks = null) {
  const mode = view?.mode, outlines = view?.outlines, tone = view?.tone;
  useEffect(() => {
    const style = drawingStyleFor({ mode, outlines, tone });
    editor?.setDrawingStyle?.(style);
    checks?.setDrawingStyle?.(style);
  }, [editor, checks, mode, outlines, tone]);
}
